import React, { useState } from 'react';
import {
  BugReportOutlined,
  ChatBubbleOutline,
  ChevronRight,
  EmojiObjectsOutlined,
  ExpandMore,
  HelpOutline,
  ReceiptOutlined,
} from 'Components/Atoms/Icons';
import useLanguage from 'CustomHooks/useLanguage';
import {
  SupportConversationDocument,
  SupportConversationTemplate,
  SupportTicketStatus,
} from 'types/shared/documents/support';
import { RestaurantTicketSummary } from 'types/shared/routes/v03/support';
import {
  isConversationUnread,
  isOpenForRestaurant,
} from 'CustomHooks/Support/useSupportData';
import {
  conversationStatusLabel,
  formatSupportTime,
  resolveTr,
  ticketStatusLabel,
  ticketTypeLabel,
} from './helpers';

const OPEN_TICKET_STATUSES: SupportTicketStatus[] = [
  'backlog',
  'open',
  'in_progress',
  'needs_review',
];

// `icon` is a free-form string from the server-side template list.
const starterIcon = (icon?: string) => {
  switch (icon) {
    case 'bug':
      return <BugReportOutlined />;
    case 'lightbulb':
      return <EmojiObjectsOutlined />;
    case 'help':
      return <HelpOutline />;
    case 'receipt':
      return <ReceiptOutlined />;
    default:
      return <ChatBubbleOutline />;
  }
};

type Props = {
  conversations: SupportConversationDocument[];
  tickets: RestaurantTicketSummary[];
  templates: SupportConversationTemplate[];
  templatesDisabled: boolean;
  currentUid: string;
  // Owned by the panel: this component unmounts whenever a ticket or chat
  // opens, so keeping the expanded state here would collapse both lists on
  // every way back.
  ticketsExpanded: boolean;
  conversationsExpanded: boolean;
  onToggleTickets: () => void;
  onToggleConversations: () => void;
  onStartTemplate: (template: SupportConversationTemplate) => void;
  onOpenConversation: (conversationId: string) => void;
  onOpenTicket: (ticketId: string) => void;
  onOpenChangelog: () => void;
};

const SupportOverview = ({
  conversations,
  tickets,
  templates,
  templatesDisabled,
  currentUid,
  ticketsExpanded,
  conversationsExpanded,
  onToggleTickets,
  onToggleConversations,
  onStartTemplate,
  onOpenConversation,
  onOpenTicket,
  onOpenChangelog,
}: Props) => {
  const { language } = useLanguage();
  const [ticketTab, setTicketTab] = useState<'open' | 'closed'>('open');
  const [convTab, setConvTab] = useState<'open' | 'closed'>('open');

  const openTickets = tickets.filter((t) =>
    OPEN_TICKET_STATUSES.includes(t.status),
  );
  const closedTickets = tickets.filter(
    (t) => !OPEN_TICKET_STATUSES.includes(t.status),
  );
  const shownTickets = ticketTab === 'open' ? openTickets : closedTickets;

  const openConversations = conversations.filter(isOpenForRestaurant);
  const closedConversations = conversations.filter(
    (c) => !isOpenForRestaurant(c),
  );
  const shownConversations =
    convTab === 'open' ? openConversations : closedConversations;

  const hasUnread = openConversations.some((c) =>
    isConversationUnread(c, currentUid),
  );

  return (
    <div className="support-overview">
      <div className="support-overview__intro">
        <p className="support-overview__greeting">
          Hallo! Wie können wir dir helfen?
        </p>
        <p className="support-overview__subtitle">
          Wähle ein Thema oder schreib uns unten direkt.
        </p>
      </div>

      {!!templates.length && (
        <div className="support-starters">
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              className="support-starter"
              disabled={templatesDisabled}
              onClick={() => onStartTemplate(template)}
            >
              <span className="support-starter__icon">
                {starterIcon(template.icon)}
              </span>
              <span className="support-starter__text">
                <span className="support-starter__label">
                  {resolveTr(language, template.label)}
                </span>
                {!!template.description && (
                  <span className="support-starter__description">
                    {resolveTr(language, template.description)}
                  </span>
                )}
              </span>
              <ChevronRight className="support-starter__chevron" />
            </button>
          ))}
        </div>
      )}

      <button type="button" className="support-news" onClick={onOpenChangelog}>
        <span className="support-news__label">Neuigkeiten</span>
        <span className="support-news__description">
          Was sich bei Gastronaut verbessert hat
        </span>
      </button>

      {!!tickets.length && (
        <section className="support-section support-section--tickets">
          <h4 className="support-section__title">
            <button
              type="button"
              className="support-section__toggle"
              aria-expanded={ticketsExpanded}
              onClick={onToggleTickets}
            >
              <ExpandMore
                className={`support-section__chevron${
                  ticketsExpanded ? ' support-section__chevron--open' : ''
                }`}
              />
              Tickets
              <span className="support-section__count">
                {openTickets.length} offen
              </span>
            </button>
          </h4>
          {ticketsExpanded && (
            <div className="support-section__body">
              <div className="support-tabs">
                <button
                  type="button"
                  className={`support-tab${
                    ticketTab === 'open' ? ' support-tab--active' : ''
                  }`}
                  onClick={() => setTicketTab('open')}
                >
                  Offen ({openTickets.length})
                </button>
                <button
                  type="button"
                  className={`support-tab${
                    ticketTab === 'closed' ? ' support-tab--active' : ''
                  }`}
                  onClick={() => setTicketTab('closed')}
                >
                  Geschlossen ({closedTickets.length})
                </button>
              </div>
              <div className="support-tiles">
                {shownTickets.length ? (
                  shownTickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      type="button"
                      className={`support-tile support-tile--ticket support-tile--status-${ticket.status}`}
                      onClick={() => onOpenTicket(ticket.id)}
                    >
                      <span className="support-tile__title">
                        {ticket.title}
                      </span>
                      <span className="support-tile__meta">
                        {ticketTypeLabel(ticket.type)} ·{' '}
                        {ticketStatusLabel(ticket.status)} ·{' '}
                        {formatSupportTime(
                          ticket.updatedAt ?? ticket.createdAt,
                        )}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="support-empty">
                    Keine {ticketTab === 'open' ? 'offenen' : 'geschlossenen'}{' '}
                    Tickets.
                  </p>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {!!conversations.length && (
        <section className="support-section">
          <h4 className="support-section__title">
            <button
              type="button"
              className="support-section__toggle"
              aria-expanded={conversationsExpanded}
              onClick={onToggleConversations}
            >
              <ExpandMore
                className={`support-section__chevron${
                  conversationsExpanded ? ' support-section__chevron--open' : ''
                }`}
              />
              Gespräche
              {hasUnread && (
                <span
                  className="support-unread-dot"
                  role="img"
                  aria-label="ungelesene Nachrichten"
                />
              )}
              <span className="support-section__count">
                {openConversations.length} offen
              </span>
            </button>
          </h4>
          {conversationsExpanded && (
            <div className="support-section__body">
              <div className="support-tabs">
                <button
                  type="button"
                  className={`support-tab${
                    convTab === 'open' ? ' support-tab--active' : ''
                  }`}
                  onClick={() => setConvTab('open')}
                >
                  Offen ({openConversations.length})
                </button>
                <button
                  type="button"
                  className={`support-tab${
                    convTab === 'closed' ? ' support-tab--active' : ''
                  }`}
                  onClick={() => setConvTab('closed')}
                >
                  Geschlossen ({closedConversations.length})
                </button>
              </div>
              <div className="support-tiles">
                {shownConversations.length ? (
                  shownConversations.map((conversation) => {
                    const unread = isConversationUnread(
                      conversation,
                      currentUid,
                    );
                    return (
                      <button
                        key={conversation.id}
                        type="button"
                        className={`support-tile${
                          unread ? ' support-tile--unread' : ''
                        }`}
                        onClick={() => onOpenConversation(conversation.id)}
                      >
                        <span className="support-tile__row">
                          <span className="support-tile__title">
                            {conversation.subject || 'Unterhaltung'}
                          </span>
                          {unread && (
                            <span
                              className="support-unread-dot"
                              role="img"
                              aria-label="ungelesen"
                            />
                          )}
                        </span>
                        {conversation.lastMessage?.preview && (
                          <span className="support-tile__preview">
                            {conversation.lastMessage.preview}
                          </span>
                        )}
                        <span className="support-tile__meta">
                          {conversationStatusLabel(conversation.status)} ·{' '}
                          {formatSupportTime(
                            conversation.updatedAt ?? conversation.createdAt,
                          )}
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <p className="support-empty">
                    Keine {convTab === 'open' ? 'offenen' : 'geschlossenen'}{' '}
                    Gespräche.
                  </p>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default SupportOverview;
